/*============================================================================
  HC - IB desktop brands menu controller - 2026-09-27

  Drives snippets/ib-brands-menu.liquid. Loaded from layout/theme.liquid only
  when settings.brands_menu_v2 is on, after theme.js and
  assets/ib-menu-sheet.js.

  theme.js still opens and closes the menu: the BRANDS button is a stock
  [data-mega-menu-trigger], and theme.headerNav toggles .is-focused on its
  <li> (click, click outside, Escape). The menu sheet (window.IBMenuSheet,
  shared with the department menus) watches that class, holds the page
  still while the menu is open, closes it when it should, and gives keys and
  focus back as it should. This script adds what is the brands' own:
  - Brand logos load on intent: when the pointer or focus reaches BRANDS,
    or the menu opens. Until then their URLs wait in data attributes, so a
    closed menu costs no image downloads.
  - The A-Z index. Liquid emits one flat list in menu order; this sorts it
    accent-aware, groups it by letter ('sort_natural' is not accent-aware),
    fills the letter bar (a star for Elite Brands, then A-Z) and keeps it on
    the section in view. A letter jump flashes the section it lands on.
  - Brand search: filters as you type and takes over the whole sheet, the
    matches moved into one list, best first. Typing anywhere while the menu
    is open starts a search, '/' focuses it, Enter opens the first match,
    Escape clears it before it closes the menu, arrow keys move between
    brands.
  - The menu is a sheet attached under the nav bar, sized to fill the window
    below it. While it is open, <html> carries ib-bm-open (the sheet's
    pageClass), which hides the product pages' sticky buy bars
    (assets/ib-product.css, assets/product-education.css.liquid): fixed to
    the bottom of the window, they would cover the sheet's last row.
  - Until it has set the menu up (is-ready on the root), the CSS shows each
    brand by name: the logos wait on this script, so without it (blocked, or
    failed at boot) the tiles would be blank.
  Rows that assets/ib-brand-menu-regions.js hid for the shopper's region are
  never counted, shown or offered as a match.

  The text helpers (sorting, matching, ranking) are assets/ib-brand-text.js,
  loaded before this; the mobile menu keeps a copy, and tests/brands-menu
  checks they agree. Re-exported on window.IBBrandsMenu for the tests. ES5
  on purpose (no build step).
==============================================================================*/
(function () {
  'use strict';

  var SELECTORS = {
    root: '[data-ib-brands-menu]',
    search: '[data-ib-bm-search]',
    clear: '[data-ib-bm-clear]',
    close: '[data-ib-bm-close]',
    status: '[data-ib-bm-status]',
    total: '[data-ib-bm-total]',
    elite: '[data-ib-bm-elite]',
    letters: '[data-ib-bm-letters]',
    jump: '[data-ib-bm-jump]',
    scroller: '[data-ib-bm-scroller]',
    source: '[data-ib-bm-source]',
    groups: '[data-ib-bm-groups]',
    group: '[data-ib-bm-letter]',
    brand: '[data-ib-bm-brand]',
    name: '[data-ib-bm-name]',
    empty: '[data-ib-bm-empty]',
    query: '[data-ib-bm-query]',
    found: '[data-ib-bm-found]',
    lazyImg: 'img[data-ib-bm-src]',
    logo: '.ib-bm__logo',
    art: 'img[data-ib-bm-art]',
    artBox: '.ib-bm__art'
  };

  var PAGE_OPEN_CLASS = 'ib-bm-open'; // on <html> while the menu is open
  var REGION_HIDDEN_ATTR = 'data-ib-region-hidden';
  var LIVE_ATTR = 'data-ib-bm-live';
  var ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  // The letter bar's first button, for Elite Brands.
  var ELITE_KEY = 'elite';
  var STAR = '\u2605';
  // A letter jump holds the bar on that key until the list stops moving
  // (no scroll event for JUMP_SETTLE_MS), or JUMP_MAX_MS at the outside: a
  // long smooth scroll on a slow machine can run near a second.
  var JUMP_SETTLE_MS = 140;
  var JUMP_MAX_MS = 1300;
  // How long the landing flash runs (the CSS animations fit inside it).
  var FLASH_MS = 1500;
  var FLASH_CLASS = 'is-flash';
  // Room between a jumped-to letter and the top of the list.
  var JUMP_OFFSET = 10;
  // The sheet runs to the bottom of the window and never past it, however
  // short that leaves it: nothing scrolls the page behind it, so a taller
  // sheet would put its last brands out of reach. Below SHORT_AVAIL the
  // letter bar steps aside (is-short) so the list keeps room; search still
  // finds every brand.
  var SHORT_AVAIL = 360;
  // How far ahead of the visible part of the list A-Z logos start loading.
  var ROW_PRELOAD_MARGIN = '600px 0px';

  /* ------------------------------------------------------------------------
     Text helpers: assets/ib-brand-text.js (window.IBBrandText), shared with
     the promotions page's brand filter. Without it the menu doesn't start.
  ------------------------------------------------------------------------ */

  var text = window.IBBrandText || {};
  var normalizeText = text.normalizeText;
  var brandSortKey = text.brandSortKey;
  var matchesQuery = text.matchesQuery;
  var matchRank = text.matchRank;
  var sortBrands = text.sortBrands;
  var groupByLetter = text.groupByLetter;

  /* ------------------------------------------------------------------------
     Small DOM utilities
  ------------------------------------------------------------------------ */

  function toArray(list) {
    var out = [];
    if (!list) return out;
    for (var i = 0; i < list.length; i++) out.push(list[i]);
    return out;
  }

  function closest(el, selector) {
    if (!el || el.nodeType !== 1) return null;
    if (typeof el.closest === 'function') return el.closest(selector);
    for (var n = el; n && n.nodeType === 1; n = n.parentNode) {
      if (n.matches && n.matches(selector)) return n;
    }
    return null;
  }

  function isEditable(el) {
    if (!el || el.nodeType !== 1) return false;
    var tag = el.localName || String(el.tagName || '').toLowerCase();
    if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
    return el.isContentEditable === true;
  }

  function focusQuietly(el) {
    if (!el || typeof el.focus !== 'function') return;
    try {
      el.focus({ preventScroll: true });
    } catch (e) {
      el.focus();
    }
  }

  // Moves a waiting logo's URL into src. 'is-loaded' fades it in. A
  // logo that fails (a file since deleted from admin Files) gives way to the
  // brand's name, the wordmark a brand without a logo shows.
  function loadImage(img) {
    var src = img.getAttribute('data-ib-bm-src');
    if (!src) return;
    var done = function () {
      img.classList.add('is-loaded');
    };
    var failed = function () {
      var logo = closest(img, SELECTORS.logo);
      var tile = logo && closest(logo, SELECTORS.brand);
      var name = tile && tile.querySelector(SELECTORS.name);
      if (!logo || !name) return done();
      logo.className = 'ib-bm__logo ib-bm__logo--text';
      logo.textContent = name.textContent;
    };
    img.addEventListener('load', done);
    img.addEventListener('error', failed);
    img.setAttribute('src', src);
    img.removeAttribute('data-ib-bm-src');
  }

  // Moves a tile's hover picture into src. The markup carries only its 344px
  // URL (the retina size; every tile holds one, so a second URL each would
  // weigh on every page's HTML), and the 172px one for an ordinary screen is
  // the same URL with the CDN's width changed. The CSS shows the picture only
  // once 'is-loaded' is on its box, so a hover before it arrives keeps the
  // logo; a picture that fails takes its box with it, and the tile stays a
  // logo.
  function loadArt(img) {
    var src = img.getAttribute('data-ib-bm-art');
    if (!src) return;
    var box = closest(img, SELECTORS.artBox);
    img.addEventListener('load', function () {
      if (box) box.classList.add('is-loaded');
    });
    img.addEventListener('error', function () {
      if (box && box.parentNode) box.parentNode.removeChild(box);
    });
    var small = src.replace(/([?&]width=)344(?=&|$)/, '$1172');
    if (small !== src) img.setAttribute('srcset', small + ' 172w, ' + src + ' 344w');
    img.setAttribute('src', src);
    img.removeAttribute('data-ib-bm-art');
  }

  function prefersReducedMotion() {
    return Boolean(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  // Opening the menu moves focus into the search only with a mouse or
  // trackpad. On a touchscreen that would throw up the keyboard over the menu.
  function hasFinePointer() {
    return Boolean(window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches);
  }

  /* ------------------------------------------------------------------------
     Controller
  ------------------------------------------------------------------------ */

  function IBBrandsMenu(root) {
    this.root = root;
    this.input = root.querySelector(SELECTORS.search);
    this.clearBtns = toArray(root.querySelectorAll(SELECTORS.clear));
    this.status = root.querySelector(SELECTORS.status);
    this.totalEl = root.querySelector(SELECTORS.total);
    this.elite = root.querySelector(SELECTORS.elite);
    this.letters = root.querySelector(SELECTORS.letters);
    this.scroller = root.querySelector(SELECTORS.scroller);
    this.emptyEl = root.querySelector(SELECTORS.empty);
    this.queryEl = root.querySelector(SELECTORS.query);
    this.foundEl = root.querySelector(SELECTORS.found);

    this.hydrated = false;
    this.query = '';
    this.activeLetter = '';
    this.jumping = false;
    this.jumpTimer = null;
    this.jumpCap = null;
    this.pendingFlash = null;
    this.flashed = null;
    this.flashTimer = null;
    this.scrollFrame = null;
    this.swallowEscapeUp = false;
    this.rowObserver = null;

    this.buildIndex();
    this.buildLetters();
    this.sheet = window.IBMenuSheet.create(this, {
      root: root,
      pageClass: PAGE_OPEN_CLASS,
      scroller: this.scroller,
      closers: [root.querySelector(SELECTORS.close)],
      links: SELECTORS.brand
    });
    this.bind();
    this.applyFilter('');
    this.sheet.sync();
    // The CSS shows the logos, and not each brand's name, from here on.
    root.classList.add('is-ready');
  }

  IBBrandsMenu.prototype = {
    destroy: function () {
      this.sheet.destroy();
      if (this.rowObserver) this.rowObserver.disconnect();
      this.rowObserver = null;
      clearTimeout(this.jumpTimer);
      clearTimeout(this.jumpCap);
      clearTimeout(this.flashTimer);
      if (this.scrollFrame) {
        var cancel = window.cancelAnimationFrame || clearTimeout; // as onScroll scheduled it
        cancel(this.scrollFrame);
        this.scrollFrame = null;
      }
    },

    /* ---------------- index ---------------- */

    /*
      Moves the source list's <li> nodes into letter groups rather than
      re-creating them, so whatever the region script already did to a row
      (inline display:none + data-ib-region-hidden) stays, and the rows it
      hides later are still inside #HeaderWrapper where it looks.
    */
    buildIndex: function () {
      var source = this.root.querySelector(SELECTORS.source);
      var host = this.root.querySelector(SELECTORS.groups);

      if (source && host) {
        var rows = toArray(source.querySelectorAll(SELECTORS.brand));
        var items = [];
        for (var i = 0; i < rows.length; i++) {
          var anchor = rows[i].querySelector('a');
          items.push({
            el: rows[i],
            title: rows[i].getAttribute('data-ib-bm-title') || (anchor ? anchor.textContent : ''),
            href: anchor ? anchor.getAttribute('href') || '' : ''
          });
        }

        var groups = groupByLetter(sortBrands(items));
        for (var g = 0; g < groups.length; g++) {
          var section = document.createElement('section');
          section.className = 'ib-bm__group';
          section.setAttribute('data-ib-bm-letter', groups[g].letter);

          var head = document.createElement('h4');
          head.className = 'ib-bm__letter';
          head.textContent = groups[g].letter;
          section.appendChild(head);

          var list = document.createElement('ul');
          list.className = 'ib-bm__rows';
          list.setAttribute('role', 'list');
          for (var k = 0; k < groups[g].items.length; k++) {
            list.appendChild(groups[g].items[k].el);
          }
          section.appendChild(list);
          host.appendChild(section);
        }
        if (source.parentNode) source.parentNode.removeChild(source);
      }

      this.groups = toArray(this.root.querySelectorAll(SELECTORS.group));
      this.tiles = this.elite ? toArray(this.elite.querySelectorAll(SELECTORS.brand)) : [];
      this.rows = [];
      for (var r = 0; r < this.groups.length; r++) {
        this.rows = this.rows.concat(toArray(this.groups[r].querySelectorAll(SELECTORS.brand)));
      }
      // Each row's own letter group, where it goes back to after a search.
      this.homes = [];
      for (var h = 0; h < this.rows.length; h++) this.homes.push(this.rows[h].parentNode);

      // The search results: the matches move here while searching (arrange).
      this.results = null;
      if (host) {
        this.results = document.createElement('ul');
        this.results.className = 'ib-bm__rows ib-bm__results';
        this.results.setAttribute('role', 'list');
        this.results.hidden = true;
        host.appendChild(this.results);
      }

      // Rows for the Elite Brands get the gold star (CSS), matched by URL.
      var eliteHrefs = {};
      for (var c = 0; c < this.tiles.length; c++) {
        var tileLink = this.tiles[c].querySelector('a');
        if (tileLink) eliteHrefs[tileLink.getAttribute('href') || ''] = true;
      }
      for (var e = 0; e < this.rows.length; e++) {
        var rowLink = this.rows[e].querySelector('a');
        var href = rowLink ? rowLink.getAttribute('href') || '' : '';
        if (href && eliteHrefs[href]) this.rows[e].setAttribute('data-ib-bm-elite-row', '');
      }
      var all = this.tiles.concat(this.rows);
      for (var n = 0; n < all.length; n++) {
        var el = all[n];
        el.setAttribute('data-ib-bm-key', brandSortKey(el.getAttribute('data-ib-bm-title') || el.textContent || ''));
      }
    },

    // A star for Elite Brands (when there are any), then one button per
    // letter plus '#', always all of them so the bar never shifts as a
    // search empties letters; empty ones are disabled.
    buildLetters: function () {
      this.jumpBtns = [];
      if (!this.letters || !this.groups.length) return;
      var keys = ALPHABET.split('');
      keys.push('#');
      if (this.elite && this.tiles.length) keys.unshift(ELITE_KEY);
      for (var i = 0; i < keys.length; i++) {
        var btn = document.createElement('button');
        btn.className = keys[i] === ELITE_KEY ? 'ib-bm__jump ib-bm__jump--elite' : 'ib-bm__jump';
        btn.setAttribute('type', 'button');
        btn.setAttribute('tabindex', '-1');
        btn.setAttribute('data-ib-bm-jump', keys[i]);
        btn.textContent = keys[i] === ELITE_KEY ? STAR : keys[i];
        this.letters.appendChild(btn);
        this.jumpBtns.push(btn);
      }
      this.letters.hidden = false;
    },

    // The section a letter bar key scrolls to: Elite Brands or a letter group.
    groupFor: function (key) {
      if (key === ELITE_KEY) return this.elite && this.tiles.length ? this.elite : null;
      for (var i = 0; i < this.groups.length; i++) {
        if (this.groups[i].getAttribute('data-ib-bm-letter') === key) return this.groups[i];
      }
      return null;
    },

    /* ---------------- search ---------------- */

    applyFilter: function (raw) {
      var text = raw === null || raw === undefined ? '' : String(raw);
      var query = normalizeText(text);
      var changed = query !== this.query;
      this.query = query;

      var shown = 0;
      var total = 0;
      var i;

      for (i = 0; i < this.rows.length; i++) {
        var row = this.rows[i];
        var regionHidden = row.hasAttribute(REGION_HIDDEN_ATTR);
        var match = matchesQuery(row.getAttribute('data-ib-bm-key') || '', query);
        // Only 'hidden' is ours; the region script's inline display:none stays.
        row.hidden = !match;
        if (!regionHidden) total += 1;
        if (match && !regionHidden) {
          shown += 1;
          row.setAttribute(LIVE_ATTR, '');
        } else {
          row.removeAttribute(LIVE_ATTR);
        }
      }
      if (changed) this.arrange();

      var tilesLive = 0;
      for (i = 0; i < this.tiles.length; i++) {
        if (!this.tiles[i].hasAttribute(REGION_HIDDEN_ATTR)) tilesLive += 1;
      }
      if (this.elite) this.elite.hidden = Boolean(query) || tilesLive === 0;

      for (i = 0; i < this.groups.length; i++) {
        this.groups[i].hidden = !this.groups[i].querySelector('[' + LIVE_ATTR + ']');
      }
      for (i = 0; i < this.jumpBtns.length; i++) {
        var group = this.groupFor(this.jumpBtns[i].getAttribute('data-ib-bm-jump'));
        this.jumpBtns[i].disabled = !group || group.hidden;
      }

      var none = Boolean(query) && shown === 0;
      this.root.classList.toggle('is-searching', Boolean(query));
      if (this.emptyEl) this.emptyEl.hidden = !none;
      if (this.foundEl) this.foundEl.textContent = query ? '\u00B7 ' + shown + (shown === 1 ? ' match' : ' matches') : '';
      if (this.queryEl) this.queryEl.textContent = query ? '\u201C' + text.replace(/^\s+|\s+$/g, '') + '\u201D' : '';
      if (this.clearBtns[0]) this.clearBtns[0].hidden = !text;
      if (this.totalEl) this.totalEl.textContent = String(total);
      if (this.input) this.input.setAttribute('placeholder', 'Search ' + total + ' brands');
      if (this.status) {
        this.status.textContent = !query ? '' : shown === 0 ? 'No brands match' : shown + (shown === 1 ? ' brand' : ' brands');
      }

      if (changed && this.scroller) this.scroller.scrollTop = 0;
      this.syncActiveLetter();
      return shown;
    },

    /*
      While searching, the matches run as one grid, best first (matchRank,
      then A-Z), in the results list: moved there in that order rather than
      ordered by CSS, which would leave Tab going through them A-Z while the
      screen and the arrow keys go best first. Every row goes back to its
      letter group first, so the groups are in A-Z order again once the search
      is cleared, and hide while it runs (nothing live left in them). Only on
      a change of search: a focused row that moves loses focus, and when the
      search changes, focus is in the search box.
    */
    arrange: function () {
      var i;
      for (i = 0; i < this.rows.length; i++) {
        if (this.homes[i]) this.homes[i].appendChild(this.rows[i]);
      }
      if (!this.results) return;
      var query = this.query;
      var hits = [];
      for (i = 0; query && i < this.rows.length; i++) {
        if (this.rows[i].hidden) continue;
        hits.push({ el: this.rows[i], rank: matchRank(this.rows[i].getAttribute('data-ib-bm-key') || '', query), at: i });
      }
      hits.sort(function (a, b) {
        return a.rank - b.rank || a.at - b.at;
      });
      for (i = 0; i < hits.length; i++) this.results.appendChild(hits[i].el);
      this.results.hidden = !query;
    },

    clearSearch: function (refocus) {
      if (this.input) {
        this.input.value = '';
        if (refocus) focusQuietly(this.input);
      }
      this.applyFilter('');
    },

    // Brands the shopper can see right now, in reading order: the Elite
    // tiles (not while searching), then the A-Z rows, or the results in the
    // order they show.
    liveLinks: function () {
      var out = [];
      var i;
      if (this.elite && !this.elite.hidden) {
        for (i = 0; i < this.tiles.length; i++) {
          if (!this.tiles[i].hasAttribute(REGION_HIDDEN_ATTR)) out.push(this.tiles[i].querySelector('a'));
        }
      }
      var rows = this.query && this.results ? toArray(this.results.querySelectorAll(SELECTORS.brand)) : this.rows;
      for (i = 0; i < rows.length; i++) {
        if (rows[i].hasAttribute(LIVE_ATTR)) out.push(rows[i].querySelector('a'));
      }
      return out;
    },

    // The first result on screen, which Enter opens.
    firstMatch: function () {
      var links = this.liveLinks();
      return links.length ? links[0] : null;
    },

    /* ---------------- letters ---------------- */

    jumpTo: function (letter) {
      var group = this.groupFor(letter);
      if (!group || group.hidden || !this.scroller) return;
      var top = Math.max(0, (group.offsetTop || 0) - JUMP_OFFSET);
      var self = this;

      this.setActiveLetter(letter);
      this.pendingFlash = group;

      // Already there (or as far as the list goes): nothing will scroll, so
      // no scroll event will say it landed. Flash now.
      var sh = this.scroller.scrollHeight;
      var ch = this.scroller.clientHeight;
      var target = sh && ch ? Math.min(top, sh - ch) : top;
      if (Math.abs((this.scroller.scrollTop || 0) - target) < 2) {
        this.landed();
        return;
      }

      this.jumping = true;
      clearTimeout(this.jumpCap);
      this.jumpCap = setTimeout(function () {
        self.landed();
      }, JUMP_MAX_MS);

      if (typeof this.scroller.scrollTo === 'function') {
        try {
          this.scroller.scrollTo({ top: top, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
          return;
        } catch (e) {
          /* fall through */
        }
      }
      this.scroller.scrollTop = top;
    },

    // The jump has finished moving: release the bar and flash where it landed.
    landed: function () {
      this.jumping = false;
      clearTimeout(this.jumpTimer);
      clearTimeout(this.jumpCap);
      var group = this.pendingFlash;
      this.pendingFlash = null;
      if (group && !group.hidden) this.flash(group);
    },

    /*
      Marks the section a letter jump landed on so the eye finds it: the CSS
      pulses its background twice, pops its letter, and ripples its logos
      one after another in reading order - hence each brand's --ib-bm-r.
      Re-adding the class after a reflow restarts the animation when the same
      letter is clicked again.
    */
    flash: function (group) {
      var self = this;
      if (this.flashed && this.flashed !== group) this.flashed.classList.remove(FLASH_CLASS);
      var n = 0;
      var brands = toArray(group.querySelectorAll(SELECTORS.brand));
      for (var i = 0; i < brands.length; i++) {
        if (brands[i].hidden || brands[i].hasAttribute(REGION_HIDDEN_ATTR)) continue;
        if (brands[i].style && typeof brands[i].style.setProperty === 'function') {
          brands[i].style.setProperty('--ib-bm-r', String(n));
        }
        n += 1;
      }
      group.classList.remove(FLASH_CLASS);
      void group.offsetWidth; // restart the animation
      group.classList.add(FLASH_CLASS);
      this.flashed = group;
      clearTimeout(this.flashTimer);
      this.flashTimer = setTimeout(function () {
        group.classList.remove(FLASH_CLASS);
        if (self.flashed === group) self.flashed = null;
      }, FLASH_MS);
    },

    setActiveLetter: function (letter) {
      if (letter === this.activeLetter) return;
      this.activeLetter = letter;
      for (var i = 0; i < this.jumpBtns.length; i++) {
        this.jumpBtns[i].classList.toggle('is-active', this.jumpBtns[i].getAttribute('data-ib-bm-jump') === letter);
      }
    },

    // The bar follows the list: the star while Elite Brands is above the
    // first letter, then the last letter whose heading has reached the top,
    // or the last letter once the list is scrolled to its end.
    syncActiveLetter: function () {
      if (!this.scroller) return;
      this.root.classList.toggle('is-scrolled', this.scroller.scrollTop > 2);
      if (this.jumping) return;
      var visible = [];
      for (var i = 0; i < this.groups.length; i++) {
        if (!this.groups[i].hidden) visible.push(this.groups[i]);
      }
      if (!visible.length) {
        this.setActiveLetter('');
        return;
      }
      var top = this.scroller.scrollTop;
      var reached = function (el) {
        return (el.offsetTop || 0) - JUMP_OFFSET - 2 <= top;
      };
      var active = null;
      for (var j = 0; j < visible.length; j++) {
        if (reached(visible[j])) active = visible[j];
      }
      var sh = this.scroller.scrollHeight;
      var ch = this.scroller.clientHeight;
      if (sh && ch && top > 0 && top + ch >= sh - 2) active = visible[visible.length - 1];
      if (!active) {
        var eliteShown = this.elite && !this.elite.hidden && this.tiles.length;
        this.setActiveLetter(eliteShown ? ELITE_KEY : visible[0].getAttribute('data-ib-bm-letter'));
        return;
      }
      this.setActiveLetter(active.getAttribute('data-ib-bm-letter'));
    },

    onScroll: function () {
      var self = this;
      if (this.jumping) {
        this.root.classList.toggle('is-scrolled', this.scroller.scrollTop > 2);
        // Once the smooth scroll stops moving, it has landed.
        clearTimeout(this.jumpTimer);
        this.jumpTimer = setTimeout(function () {
          self.landed();
        }, JUMP_SETTLE_MS);
        return;
      }
      if (this.scrollFrame) return;
      var raf = window.requestAnimationFrame || function (fn) { return setTimeout(fn, 16); };
      this.scrollFrame = raf(function () {
        self.scrollFrame = null;
        self.syncActiveLetter();
      });
    },

    /* ---------------- open / close (the sheet calls these) ---------------- */

    // After hydrate() and measure().
    opened: function () {
      // Region hiding may have landed since the last look.
      this.applyFilter(this.input ? this.input.value : '');
      var active = document.activeElement;
      if (this.input && hasFinePointer() && (!active || active === document.body || active === this.sheet.trigger)) {
        focusQuietly(this.input);
      }
    },

    // After the fade-out, before the list goes back to the top.
    reset: function () {
      if (this.input && this.input.value) this.clearSearch(false);
    },

    // The letter bar follows the reflowed list.
    resized: function () {
      this.syncActiveLetter();
    },

    /*
      Hover pictures load on intent, a letter row at a time: when the pointer
      (a mouse or pen; on touch there's no hover to show them) or focus
      reaches a tile, every picture in its row - Elite Brands, its letter's
      group, or the search results - starts, so the tiles beside it are ready
      when the pointer moves on.
    */
    warmArt: function (target) {
      var tile = closest(target, SELECTORS.brand);
      if (!tile || !this.root.contains(tile)) return;
      var results = this.results && this.results.contains(tile) ? this.results : null;
      var row = closest(tile, SELECTORS.group) || closest(tile, SELECTORS.elite) || results || tile;
      var imgs = toArray(row.querySelectorAll(SELECTORS.art));
      for (var i = 0; i < imgs.length; i++) loadArt(imgs[i]);
    },

    /*
      Starts the logos, once: the Elite tiles straight away (they are the
      first thing the menu shows), the A-Z tiles as the list comes within
      ROW_PRELOAD_MARGIN of them. That observer is rooted on the list itself,
      because a row scrolled out of that box is out of view however near the
      viewport it is - loading="lazy" goes by the viewport, so it waited until
      a row was on screen and showed a blank square while a letter jump
      scrolled past.
    */
    hydrate: function () {
      if (this.hydrated) return;
      this.hydrated = true;
      var imgs = toArray(this.root.querySelectorAll(SELECTORS.lazyImg));
      var later = [];
      var i;
      for (i = 0; i < imgs.length; i++) {
        var elite = this.elite && this.elite.contains(imgs[i]);
        if (!elite && this.scroller && this.scroller.contains(imgs[i])) later.push(imgs[i]);
        else loadImage(imgs[i]);
      }
      if (!later.length) return;

      if (typeof window.IntersectionObserver !== 'function') {
        for (i = 0; i < later.length; i++) loadImage(later[i]);
        return;
      }
      var observer = new window.IntersectionObserver(function (entries) {
        for (var e = 0; e < entries.length; e++) {
          if (!entries[e].isIntersecting) continue;
          observer.unobserve(entries[e].target);
          loadImage(entries[e].target);
        }
      }, { root: this.scroller, rootMargin: ROW_PRELOAD_MARGIN });
      this.rowObserver = observer;
      for (i = 0; i < later.length; i++) observer.observe(later[i]);
    },

    // The sheet fills the space under the header, whose height varies: the
    // sticky header compresses on scroll, the announcement bar can be
    // dismissed. Measured on opening and on resize.
    measure: function () {
      if (!this.root.style || typeof this.root.style.setProperty !== 'function') return;
      var rect = this.root.getBoundingClientRect();
      var h = window.innerHeight || 0;
      if (!h || !rect) return;
      var avail = Math.max(0, Math.floor(h - rect.top));
      this.root.style.setProperty('--ib-bm-avail', avail + 'px');
      this.root.classList.toggle('is-short', avail < SHORT_AVAIL);
    },

    /* ---------------- keyboard ---------------- */

    onInputKeydown: function (evt) {
      var key = evt.key;
      // A key held down repeats its keydown, not its keyup: a held Escape
      // that cleared the search keeps its one keyup from closing the menu.
      if (!evt.repeat) this.swallowEscapeUp = false;
      // A key pressed while an IME (or a dead-key accent) composes belongs to
      // the composition: Enter picks the candidate, Escape drops it. So does
      // its keyup, which theme.js would close the menu on for Escape: it is
      // kept from it (bind). Some IMEs name the key only on the keyup.
      if (evt.isComposing || evt.keyCode === 229) {
        this.swallowEscapeUp = true;
        return;
      }
      if (key === 'Escape' || key === 'Esc') {
        // First Escape clears the search; theme.js closes the menu on the
        // keyup, so that keyup is kept from reaching it.
        if (this.input.value) {
          evt.preventDefault();
          this.swallowEscapeUp = true;
          this.clearSearch(true);
        }
        return;
      }
      if (key === 'Enter') {
        var first = this.query ? this.firstMatch() : null;
        if (first) {
          evt.preventDefault();
          // Ctrl/Cmd or Shift, as on a link: a new tab, the menu left open.
          if (evt.ctrlKey || evt.metaKey || evt.shiftKey) window.open(first.getAttribute('href'), '_blank');
          else first.click();
        }
        return;
      }
      if (key === 'ArrowDown') {
        var next = this.firstInView();
        if (next) {
          evt.preventDefault();
          this.sheet.reveal(next);
        }
      }
    },

    // The first brand whose tile starts inside the list's view, so ArrowDown
    // from the search after a letter jump starts where the list is rather
    // than back at the top. The first brand when none does.
    firstInView: function () {
      var links = this.liveLinks();
      var box = this.scroller ? this.scroller.getBoundingClientRect() : null;
      for (var i = 0; box && i < links.length; i++) {
        var r = links[i].getBoundingClientRect();
        if (r.top >= box.top - 1 && r.top < box.bottom) return links[i];
      }
      return links[0] || null;
    },

    /*
      A key the sheet hands over: pressed in the open menu (BRANDS included)
      or on the page itself, where focus goes after a click on the sheet's
      blank space. The scrolling keys move the list (the sheet's scrollKey:
      only PageUp/PageDown from the search); the rest are the brands'.
    */
    keydown: function (evt, key) {
      var target = evt.target;
      var field = target === this.input || isEditable(target);
      if (this.sheet.scrollKey(evt, key, field) || field) return;

      // Escape with a search clears it, as it does in the field, and goes
      // back there: from a result, or from the page (a click on blank
      // space). The keyup theme.js closes on then lands on the field, which
      // keeps it (bind).
      if ((key === 'Escape' || key === 'Esc') && this.input && this.input.value) {
        evt.preventDefault();
        this.swallowEscapeUp = true;
        this.clearSearch(true);
        return;
      }

      if (key === '/') {
        evt.preventDefault();
        focusQuietly(this.input);
        return;
      }

      if (/^Arrow/.test(key) && target && this.root.contains(target) && closest(target, SELECTORS.brand)) {
        evt.preventDefault();
        this.moveFrom(target, key);
        return;
      }

      // Type to search: a printable key anywhere else in the menu, or on the
      // page, starts a search. It is typed in here rather than left to land
      // after the focus move, which not every browser guarantees.
      if (key.length === 1 && key !== ' ' && this.input) {
        evt.preventDefault();
        this.input.value = this.input.value + key;
        focusQuietly(this.input);
        this.applyFilter(this.input.value);
      }
    },

    /*
      Arrow keys between brands. Left/Right step through the Elite tiles or
      the A-Z list in order, never from one into the other. Up/Down go to the
      nearest brand in the row above or below, wherever it is: Down from the
      last row of tiles lands in the list. Up from the top goes back to the
      search.
    */
    moveFrom: function (from, key) {
      var links = this.liveLinks();
      if (links.indexOf(from) === -1) return;
      var pane = closest(from, SELECTORS.elite) || closest(from, '.ib-bm__index');
      var same = [];
      for (var i = 0; i < links.length; i++) {
        if (pane && pane.contains(links[i])) same.push(links[i]);
      }
      var here = same.indexOf(from);
      var next = null;

      if (key === 'ArrowRight') next = same[here + 1] || null;
      else if (key === 'ArrowLeft') next = same[here - 1] || null;
      else if (key === 'ArrowDown' || key === 'ArrowUp') {
        var down = key === 'ArrowDown';
        var r = from.getBoundingClientRect();
        var cy = r.top + r.height / 2;
        var cx = r.left + r.width / 2;
        var bestRow = null;
        var bestDx = null;
        for (var j = 0; j < links.length; j++) {
          if (links[j] === from) continue;
          var o = links[j].getBoundingClientRect();
          var oy = o.top + o.height / 2;
          var dy = down ? oy - cy : cy - oy;
          if (dy <= 4) continue;
          var dx = Math.abs(o.left + o.width / 2 - cx);
          // Nearest row first, then the brand in it closest to straight down/up.
          if (bestRow === null || dy < bestRow - 4 || (Math.abs(dy - bestRow) <= 4 && dx < bestDx)) {
            bestRow = dy;
            bestDx = dx;
            next = links[j];
          }
        }
        if (!next && !down) {
          focusQuietly(this.input);
          return;
        }
      }

      this.sheet.reveal(next);
    },

    /* ---------------- wiring ---------------- */

    // The brands' own listeners, on the sheet so its destroy() takes them.
    bind: function () {
      var self = this;
      var sheet = this.sheet;

      sheet.listen(this.root, 'pointerover', function (evt) {
        if (evt.pointerType !== 'touch') self.warmArt(evt.target);
      });
      sheet.listen(this.root, 'focusin', function (evt) {
        self.warmArt(evt.target);
      });

      this.clearBtns.forEach(function (btn) {
        sheet.listen(btn, 'click', function (evt) {
          evt.preventDefault();
          self.clearSearch(true);
        });
      });

      if (this.input) {
        sheet.listen(this.input, 'input', function () {
          self.applyFilter(self.input.value);
        });
        // Some browsers fire 'search' (not 'input') for the native clear.
        sheet.listen(this.input, 'search', function () {
          self.applyFilter(self.input.value);
        });
        sheet.listen(this.input, 'keydown', function (evt) {
          self.onInputKeydown(evt);
        });
        sheet.listen(this.input, 'keyup', function (evt) {
          if ((self.swallowEscapeUp || evt.isComposing) && (evt.key === 'Escape' || evt.key === 'Esc')) {
            self.swallowEscapeUp = false;
            evt.stopPropagation();
          }
        });
      }

      // A letter click leaves focus where it was, the search usually: the
      // bar is aria-hidden, so focus must never land in it.
      sheet.listen(this.letters, 'mousedown', function (evt) {
        evt.preventDefault();
      });
      sheet.listen(this.letters, 'click', function (evt) {
        var btn = closest(evt.target, SELECTORS.jump);
        if (!btn || btn.disabled) return;
        evt.preventDefault();
        self.jumpTo(btn.getAttribute('data-ib-bm-jump'));
      });

      sheet.listen(this.scroller, 'scroll', function () {
        self.onScroll();
      });

      // Rows hidden for the shopper's region come off the count before the
      // menu is ever opened, so the search placeholder is right.
      var regions = window.IBBrandRegions;
      if (regions && typeof regions.blocked === 'function') {
        regions.blocked().then(function () {
          setTimeout(function () {
            self.applyFilter(self.input ? self.input.value : '');
          }, 0);
        }, function () {});
      }
    }
  };

  /* ------------------------------------------------------------------------
     Public surface + boot
  ------------------------------------------------------------------------ */

  var api = {
    normalizeText: normalizeText,
    brandSortKey: brandSortKey,
    letterFor: text.letterFor,
    matchesQuery: matchesQuery,
    matchRank: matchRank,
    sortBrands: sortBrands,
    groupByLetter: groupByLetter,
    instance: null
  };

  window.IBBrandsMenu = api;

  // Without the sheet or the text helpers (blocked, or failed) the menu stays
  // a stock mega menu, each brand shown by name (the CSS, until is-ready).
  function boot() {
    if (api.instance) api.instance.destroy();
    api.instance = null;
    var root = document.querySelector(SELECTORS.root);
    if (!window.IBBrandText) return;
    if (!root || !window.IBMenuSheet) return;
    api.instance = new IBBrandsMenu(root);
  }

  boot();

  /*
    Theme editor: the header re-renders on every setting change. Listened for
    once, here, not by an instance: boot() destroys the instance and all its
    listeners, so a reload that rendered no menu (BRANDS renamed, say) would
    otherwise leave nothing to hear the next one. Boots when the reloaded
    section has the menu, or when the running instance's menu is gone.
  */
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (evt) {
      var section = evt && evt.target;
      var fresh = section && typeof section.querySelector === 'function' && section.querySelector(SELECTORS.root);
      var stale = api.instance && !document.contains(api.instance.root);
      if (fresh || stale) boot();
    });
  }
})();
